import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DocumentDTO,
  DocumentRetentionClass,
  DocumentUploadInput,
  DocumentVisibility,
  LeaseDTO,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Documents — the admin view of the unified permissioned R2 store (EPIC 7). Mirrors the canonical
 * {@link AdminTenants} pattern: a react-query list + an "Upload document" Dialog, per-row Download
 * (mint a signed URL → open it) and Delete. Bytes are uploaded base64-encoded in the JSON body
 * (the server hashes + size-caps them). A `tenant_visible` document MUST be bound to ≥1 of
 * lease/unit/tenant (the server 400s otherwise), so that rule is validated client-side before submit.
 * 0.24.0: retention-class + lease filters over the list, and lease/tenant PICKERS in the upload
 * dialog in place of raw id inputs.
 */

const DOCUMENTS_KEY = ['admin', 'documents'] as const;

const RETENTION_CLASSES: DocumentRetentionClass[] = [
  'lease',
  'financial',
  'inspection_photo',
  'notice',
  'maintenance',
  'identity',
  'general',
  'legal',
];

const VISIBILITIES: DocumentVisibility[] = ['owner_only', 'tenant_visible'];

/** Human label for a lease in pickers/filters: "Main — Alex Tenant (2025-09-01, active)". */
function leaseLabel(l: LeaseDTO): string {
  const names = l.tenants
    .map((t) => (t.tenant ? `${t.tenant.firstName} ${t.tenant.lastName}` : null))
    .filter(Boolean)
    .join(', ');
  return `${l.unitLabel ?? 'Unit'}${names ? ` — ${names}` : ''} (${l.startDate.slice(0, 10)}, ${l.status})`;
}

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Render a byte count as a compact KB/MB string, e.g. 1536 → "1.5 KB". */
function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Read a File as base64 (strip the `data:<ct>;base64,` prefix the DataURL adds). */
function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the selected file'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the selected file'));
    reader.readAsDataURL(file);
  });
}

function DocumentUploadForm({ leases, onClose }: { leases: LeaseDTO[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api.tenants.list() });
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [retentionClass, setRetentionClass] = useState<DocumentRetentionClass>('general');
  const [visibility, setVisibility] = useState<DocumentVisibility>('owner_only');
  const [leaseId, setLeaseId] = useState('');
  const [tenantId, setTenantId] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Please choose a file to upload');
      // A tenant-visible document must be reachable — bound to ≥1 of lease/unit/tenant.
      if (visibility === 'tenant_visible' && !(opt(leaseId) || opt(tenantId))) {
        throw new Error('Tenant-visible documents require a lease or a tenant');
      }
      const contentBase64 = await readFileBase64(file);
      const body: DocumentUploadInput = {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
        title: opt(title) ?? null,
        sourceType: 'upload',
        retentionClass,
        visibility,
        leaseId: opt(leaseId) ?? null,
        tenantId: opt(tenantId) ?? null,
      };
      return api.documents.upload(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY });
      onClose();
    },
  });

  return (
    <Dialog title="Upload document" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="d-file">File</label>
          <input
            id="d-file"
            type="file"
            required
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <div>
          <label htmlFor="d-title">Title (optional)</label>
          <input
            id="d-title"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Defaults to the file name"
          />
        </div>
        <div>
          <label htmlFor="d-retention">Retention class</label>
          <select
            id="d-retention"
            value={retentionClass}
            onChange={(e) => setRetentionClass(e.target.value as DocumentRetentionClass)}
          >
            {RETENTION_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="d-visibility">Visibility</label>
          <select
            id="d-visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as DocumentVisibility)}
          >
            {VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        {visibility === 'tenant_visible' && (
          <p className="muted">
            Tenant-visible documents must be linked to a lease or a tenant.
          </p>
        )}
        <div>
          <label htmlFor="d-lease">Lease (optional)</label>
          <select id="d-lease" value={leaseId} onChange={(e) => setLeaseId(e.target.value)}>
            <option value="">— None —</option>
            {leases.map((l) => (
              <option key={l.id} value={l.id}>
                {leaseLabel(l)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="d-tenant">Tenant (optional)</label>
          {tenantsQuery.isError ? (
            <StatusMessage tone="err">{errorMessage(tenantsQuery.error)}</StatusMessage>
          ) : (
            <select
              id="d-tenant"
              value={tenantId}
              disabled={tenantsQuery.isLoading}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <option value="">{tenantsQuery.isLoading ? 'Loading tenants…' : '— None —'}</option>
              {(tenantsQuery.data?.tenants ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.firstName} {t.lastName}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Uploading…' : 'Upload document'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function AdminDocuments() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: DOCUMENTS_KEY, queryFn: () => api.documents.list() });
  const leasesQuery = useQuery({ queryKey: ['admin', 'leases'], queryFn: () => api.leases.list() });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [retentionFilter, setRetentionFilter] = useState<DocumentRetentionClass | ''>('');
  const [leaseFilter, setLeaseFilter] = useState('');

  const download = useMutation({
    mutationFn: (id: string) => api.documents.signedUrl(id),
    onSuccess: (resp) => {
      window.open(resp.url, '_blank', 'noopener');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.documents.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });

  if (query.isLoading) return <Loading label="Loading documents…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const leases = leasesQuery.data?.leases ?? [];
  const leaseById = new Map(leases.map((l) => [l.id, l]));
  const documents = query.data.documents.filter(
    (d) =>
      (retentionFilter === '' || d.retentionClass === retentionFilter) &&
      (leaseFilter === '' || d.leaseId === leaseFilter),
  );

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Documents</h1>
        <button type="button" onClick={() => setUploadOpen(true)}>
          Upload document
        </button>
      </div>
      <p className="subtitle">
        Leases, receipts, notices, and photos — the permissioned document store for your portfolio.
      </p>

      <div className="row" role="group" aria-label="Filter documents">
        <div style={{ flex: 1, minWidth: '12rem' }}>
          <label htmlFor="df-retention">Retention class</label>
          <select
            id="df-retention"
            value={retentionFilter}
            onChange={(e) => setRetentionFilter(e.target.value as DocumentRetentionClass | '')}
          >
            <option value="">All</option>
            {RETENTION_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 2, minWidth: '14rem' }}>
          <label htmlFor="df-lease">Lease</label>
          <select id="df-lease" value={leaseFilter} onChange={(e) => setLeaseFilter(e.target.value)}>
            <option value="">All</option>
            {leases.map((l) => (
              <option key={l.id} value={l.id}>
                {leaseLabel(l)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {download.isError && <StatusMessage tone="err">{errorMessage(download.error)}</StatusMessage>}
      {remove.isError && <StatusMessage tone="err">{errorMessage(remove.error)}</StatusMessage>}

      {query.data.documents.length === 0 ? (
        <p className="muted">No documents yet. Choose “Upload document” to add one.</p>
      ) : documents.length === 0 ? (
        <p className="muted">No documents match these filters.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Documents</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Lease</th>
                <th scope="col">Source</th>
                <th scope="col">Retention</th>
                <th scope="col">Visibility</th>
                <th scope="col">Size</th>
                <th scope="col">Uploaded</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d: DocumentDTO) => {
                const name = d.title || d.filename;
                const lease = d.leaseId ? leaseById.get(d.leaseId) : undefined;
                return (
                  <tr key={d.id}>
                    <td>{name}</td>
                    <td>{lease ? (lease.unitLabel ?? 'Lease') : d.leaseId ? 'Lease' : '—'}</td>
                    <td>
                      {d.sourceType}
                      {d.derivedFromDocumentId && <span className="muted"> (reduced copy)</span>}
                    </td>
                    <td>{d.retentionClass}</td>
                    <td>{d.visibility}</td>
                    <td>{formatSize(d.sizeBytes)}</td>
                    <td>{formatDate(d.createdAt)}</td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="secondary"
                          disabled={download.isPending && download.variables === d.id}
                          onClick={() => download.mutate(d.id)}
                        >
                          Download<span className="visually-hidden"> {name}</span>
                        </button>
                        {d.retentionClass !== 'legal' && (
                          <button
                            type="button"
                            className="secondary"
                            disabled={remove.isPending && remove.variables === d.id}
                            onClick={() => {
                              if (window.confirm(`Delete document ${name}?`)) remove.mutate(d.id);
                            }}
                          >
                            Delete<span className="visually-hidden"> {name}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {uploadOpen && <DocumentUploadForm leases={leases} onClose={() => setUploadOpen(false)} />}
    </>
  );
}
